/**
 * Init script that installs a persistent audio-output capture bus.
 *
 * Symmetric to fake-mic-script.ts: where fake-mic patches getUserMedia to
 * route a fixed clip *into* the page, this taps everything the page plays
 * *out* and routes it into a MediaRecorder so workflows can record what
 * the app plays — TTS, voicemail playback, voice notes, call recordings.
 *
 * Mechanism:
 * - One persistent AudioContext + MediaStreamAudioDestinationNode per
 *   document (the capture bus).
 * - We patch AudioNode.prototype.connect so anything connecting to
 *   ctx.destination is also fanned into the capture bus (tee — the page's
 *   real audio still plays).
 * - We patch HTMLMediaElement.play so <audio>/<video> elements with
 *   non-WebAudio src URLs are wrapped in a MediaElementAudioSourceNode
 *   on first play and tee'd into the capture bus too.
 * - window.__audioCapture.start() arms a MediaRecorder over the bus stream.
 *   stop() flushes and returns base64 + duration metadata.
 */
export const AUDIO_CAPTURE_SCRIPT = `(() => {
  try {
    if (window.__audioCaptureInstalled) return;
    Object.defineProperty(window, '__audioCaptureInstalled', { value: true, configurable: false });

    var bus = { ctx: null, dest: null, taps: [], teedElements: new WeakSet() };

    function ensureBus() {
      if (!bus.ctx) {
        var Ctor = window.AudioContext || window.webkitAudioContext;
        bus.ctx = new Ctor();
        bus.dest = bus.ctx.createMediaStreamDestination();
      }
      if (bus.ctx.state !== 'running') {
        bus.ctx.resume().catch(function () {});
      }
      return bus;
    }

    // Patch AudioNode.connect to also fan-out anything reaching the
    // page's primary destination into our capture bus. We compare against
    // the *active* AudioContext.destination at call time because pages can
    // create their own AudioContexts.
    var AudioNodeProto = (window.AudioNode && window.AudioNode.prototype) || null;
    if (AudioNodeProto && !AudioNodeProto.__canaryCapturePatched) {
      var origConnect = AudioNodeProto.connect;
      AudioNodeProto.connect = function (target) {
        var result = origConnect.apply(this, arguments);
        try {
          if (target && typeof target === 'object' && this && this.context) {
            // Tee when target is the source node's own ctx.destination.
            if (target === this.context.destination) {
              ensureBus();
              // Cross-context: route by capturing the stream of a
              // MediaStreamDestinationNode in the source ctx, then feeding
              // it into the bus ctx.
              if (this.context !== bus.ctx) {
                if (!this.__canaryCaptureBridge) {
                  var localDest = this.context.createMediaStreamDestination();
                  origConnect.call(this, localDest);
                  var bridgeSrc = bus.ctx.createMediaStreamSource(localDest.stream);
                  bridgeSrc.connect(bus.dest);
                  this.__canaryCaptureBridge = { localDest: localDest, bridgeSrc: bridgeSrc };
                  bus.taps.push(this.__canaryCaptureBridge);
                }
              } else {
                origConnect.call(this, bus.dest);
              }
            }
          }
        } catch (e) {
          try { console.warn('[canary] capture tee failed', e); } catch (_) {}
        }
        return result;
      };
      Object.defineProperty(AudioNodeProto, '__canaryCapturePatched', {
        value: true,
        configurable: false,
      });
    }

    // Patch HTMLMediaElement.play so <audio>/<video> playback (which the
    // app may drive directly via element.src, not through WebAudio) gets
    // wrapped on first play and tee'd into the bus.
    var MediaProto = window.HTMLMediaElement && window.HTMLMediaElement.prototype;
    if (MediaProto && !MediaProto.__canaryCapturePatched) {
      var origPlay = MediaProto.play;
      MediaProto.play = function () {
        var self = this;
        try {
          if (!bus.teedElements.has(self)) {
            ensureBus();
            // Required so the source node can read across origins; many
            // blob: URLs are same-origin, but http(s) media may need this.
            try { self.crossOrigin = self.crossOrigin || 'anonymous'; } catch (_) {}
            var src = bus.ctx.createMediaElementSource(self);
            // Maintain audible playback to the speaker AND tee to the bus.
            src.connect(bus.ctx.destination);
            src.connect(bus.dest);
            bus.teedElements.add(self);
            bus.taps.push({ element: self, src: src });
          }
        } catch (e) {
          // createMediaElementSource throws if the element was already wired
          // through WebAudio; in that case the AudioNode patch above already
          // handles the tee, so we silently ignore.
        }
        return origPlay.apply(self, arguments);
      };
      Object.defineProperty(MediaProto, '__canaryCapturePatched', {
        value: true,
        configurable: false,
      });
    }

    var rec = { mr: null, chunks: [], mimeType: '', startedAt: 0 };

    function pickMimeType() {
      var candidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
      ];
      for (var i = 0; i < candidates.length; i++) {
        try {
          if (window.MediaRecorder && MediaRecorder.isTypeSupported(candidates[i])) {
            return candidates[i];
          }
        } catch (_) {}
      }
      return '';
    }

    function start(opts) {
      if (rec.mr && rec.mr.state !== 'inactive') return false;
      ensureBus();
      var mimeType = (opts && opts.mimeType) || pickMimeType();
      var timeslice = (opts && opts.timesliceMs) || 1000;
      var streamCtor = window.MediaStream;
      // Clone the bus stream so multiple recorders/exports don't fight.
      var stream = streamCtor ? new streamCtor(bus.dest.stream.getTracks()) : bus.dest.stream;
      rec.chunks = [];
      rec.mimeType = mimeType;
      rec.startedAt = Date.now();
      rec.mr = mimeType
        ? new MediaRecorder(stream, { mimeType: mimeType })
        : new MediaRecorder(stream);
      rec.mr.ondataavailable = function (ev) {
        if (ev.data && ev.data.size > 0) rec.chunks.push(ev.data);
      };
      rec.mr.start(timeslice);
      return true;
    }

    function blobToBase64(blob) {
      return new Promise(function (resolve, reject) {
        var fr = new FileReader();
        fr.onload = function () {
          // Strip data:<mime>;base64, prefix
          var s = String(fr.result || '');
          var i = s.indexOf(',');
          resolve(i >= 0 ? s.slice(i + 1) : s);
        };
        fr.onerror = function () { reject(fr.error || new Error('FileReader failed')); };
        fr.readAsDataURL(blob);
      });
    }

    async function stop() {
      if (!rec.mr) return null;
      var current = rec.mr;
      var chunks = rec.chunks;
      var mimeType = rec.mimeType;
      var startedAt = rec.startedAt;
      rec.mr = null;
      rec.chunks = [];
      await new Promise(function (resolve) {
        current.onstop = function () { resolve(); };
        if (current.state !== 'inactive') {
          try { current.stop(); } catch (e) { resolve(); }
        } else {
          resolve();
        }
      });
      var blob = new Blob(chunks, { type: mimeType || 'audio/webm' });
      var base64 = await blobToBase64(blob);
      var durationMs = Date.now() - startedAt;
      var sampleRate = bus.ctx ? bus.ctx.sampleRate : 0;
      return {
        base64: base64,
        mimeType: blob.type,
        durationMs: durationMs,
        sizeBytes: blob.size,
        sampleRate: sampleRate,
      };
    }

    async function flushPartial() {
      if (!rec.mr || rec.mr.state === 'inactive') return null;
      // requestData triggers an extra 'dataavailable' event for buffered chunks.
      try { rec.mr.requestData(); } catch (_) {}
      // Yield once so the dataavailable handler runs.
      await new Promise(function (r) { setTimeout(r, 0); });
      var blob = new Blob(rec.chunks, { type: rec.mimeType || 'audio/webm' });
      var base64 = await blobToBase64(blob);
      return {
        base64: base64,
        mimeType: blob.type,
        durationMs: Date.now() - rec.startedAt,
        sizeBytes: blob.size,
        sampleRate: bus.ctx ? bus.ctx.sampleRate : 0,
      };
    }

    Object.defineProperty(window, '__audioCapture', {
      value: { start: start, stop: stop, flushPartial: flushPartial, ensureBus: ensureBus },
      configurable: true,
    });
  } catch (err) {
    try { console.warn('[canary] audio-capture bootstrap failed', err); } catch (_) {}
  }
})();`;
